import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { z } from 'zod';
import type { ConversationType } from '@/models/conversations/conversation-session';
import { type ResponseError, responseScheme } from '@/models/response';
import { simpleLogger } from '@/services/sentry';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { useMessageStore } from '@/stores/message';
import { fetcher } from '@/utils/fetcher';
import { generateSessionKey } from '@/utils/session';

export async function markSessionRead(request: MarkSessionReadRequestType): Promise<ReadAllResponseType> {
  const { single, type, id, messageId = 0, cause } = request;

  simpleLogger({
    name: 'READ_MESSAGE',
    requestAttributes: { result: cause, single, type, id, messageId, suspicious: messageId.toString().length >= 13 },
  });

  let response: ReadAllResponseType;
  if (single) {
    response = await fetcher<ReadAllResponseType>(
      '/ReadChat',
      {
        [type === 'session' ? 'ChatSessionID' : 'GroupID']: id,
        ChatID: messageId,
      },
      { validResponseCodes: [0] },
    );
  } else {
    response = await fetcher<ReadAllResponseType>(
      type === 'session' ? '/ReadAllChat' : '/ReadAllGroupChat',
      {
        [type === 'session' ? 'ChatSessionID' : 'GroupID']: id,
        [type === 'session' ? 'ChatID' : 'GroupChatId']: 0,
      },
      { schema: readAllResponseScheme, validResponseCodes: [0] },
    );
  }

  return {
    ...response,
    ...request,
  };
}

export default function useMarkSessionRead() {
  const mutation = useMutation<ReadAllResponseType, ResponseError, MarkSessionReadRequestType>({
    mutationKey: ['/ReadChat'],
    mutationFn: (input) => markSessionRead(input),
    onMutate: ({ id, messageId = 0, single, type }) => {
      const key = generateSessionKey(type, id);
      const { clearUnreadCount } = useConversationSessionStore.getState();

      if (single) {
        useMessageStore.getState().read(messageId, type);
        clearUnreadCount(key, messageId);
      } else {
        useMessageStore.getState().readAll(type, id);
        clearUnreadCount(key);
      }
    },
    onError: undefined,
  });

  const markAllRead = useCallback(
    (type: ConversationType, id: number, messageId: number, lastReadId: number, cause?: string) => {
      const key = generateSessionKey(type, id);

      useConversationSessionStore.getState().clearUnreadCount(key);

      // NOTE: CHAT-1193 如果是新聊天室要帶 0 ，用於更新後端 IsNewSession 欄位
      if (messageId === 0 && lastReadId === 0) {
        mutation.mutate({ type, id, messageId: 0, cause });
        return;
      }

      if (messageId > 0 && lastReadId < messageId) {
        mutation.mutate({ type, id, messageId, cause });
      }
    },
    [],
  );

  const markRead = useCallback(
    (type: ConversationType, id: number, messageId: number, lastReadId: number, cause?: string) => {
      if (messageId <= 0) return;

      if (type === 'session') {
        mutation.mutate({ type, id, messageId, cause, single: true });
      } else {
        markAllRead(type, id, messageId, lastReadId, cause);
      }
    },
    [],
  );

  const markUnread = useCallback((type: ConversationType, id: number, updateCount?: number) => {
    const key = generateSessionKey(type, id);
    const { addUnread } = useConversationSessionStore.getState();

    addUnread(key, undefined, updateCount);
  }, []);

  return {
    markRead,
    markAllRead,
    markUnread,
    ...mutation,
  };
}

export interface MarkSessionReadRequestType {
  type: ConversationType;
  id: number;
  messageId: number;
  cause?: string;
  single?: boolean;
}

export const readAllResponseScheme = responseScheme.extend({
  MaxReadID: z.number().nullish(),
});

export type ReadAllResponseType = z.infer<typeof readAllResponseScheme> & MarkSessionReadRequestType;
